/**
 * Financial Readiness Checklist + Comfort Corner items.
 * Two small static catalogues kept together to avoid file sprawl.
 *
 * Spanish strings marked // review pending.
 */

export const FINANCIAL_CHECKLIST = [
  {
    id: "own-bank-account",
    title_en: "Bank account in your own name",
    title_es: "Cuenta de banco a tu nombre",
    note_en: "Even a basic checking, opened at a different bank than the joint accounts.",
    note_es: "Incluso una cuenta básica, abierta en un banco distinto al de las cuentas conjuntas.",
  },
  {
    id: "id-documents",
    title_en: "Identification documents",
    title_es: "Documentos de identidad",
    note_en: "Driver’s license, passport, birth certificate, social security card. Photographed + a printed copy stored off-site.",
    note_es: "Licencia, pasaporte, acta de nacimiento, seguro social. Fotografiados + una copia impresa fuera de casa.",
  },
  {
    id: "credit-report",
    title_en: "Credit report reviewed",
    title_es: "Reporte de crédito revisado",
    note_en: "annualcreditreport.com is free. Check for accounts you didn’t open.",
    note_es: "annualcreditreport.com es gratis. Revisa que no haya cuentas que tú no abriste.",
  },
  {
    id: "emergency-fund",
    title_en: "Emergency fund",
    title_es: "Fondo de emergencia",
    note_en: "Any amount. Even $20 in cash hidden somewhere safe counts.",
    note_es: "Cualquier cantidad. Incluso $20 en efectivo escondidos en un lugar seguro cuenta.",
  },
  {
    id: "independent-email",
    title_en: "Independent email address",
    title_es: "Correo electrónico independiente",
    note_en: "Not linked to any shared device. Use it for your banking, your hotline contacts, and Sister Signal.",
    note_es: "No vinculado a ningún dispositivo compartido. Úsalo para tu banco, líneas de ayuda y Sister Signal.",
  },
  {
    id: "phone-plan-access",
    title_en: "Phone plan access",
    title_es: "Acceso a tu plan de teléfono",
    note_en: "Know if you’re on a family plan, who pays, and how to transfer your number if needed.",
    note_es: "Sabe si estás en un plan familiar, quién lo paga, y cómo transferir tu número si lo necesitas.",
  },
  {
    id: "important-records",
    title_en: "Important records copied",
    title_es: "Registros importantes copiados",
    note_en: "Medical, school, tax, custody, marriage, and pet papers — all photographed and stored off-site.",
    note_es: "Médicos, escolares, fiscales, custodia, matrimonio, mascotas — todos fotografiados y guardados fuera de casa.",
  },
  {
    id: "password-inventory",
    title_en: "Password inventory",
    title_es: "Inventario de contraseñas",
    note_en: "List of accounts you own (email, bank, social, cloud). Change passwords on a device he can’t access.",
    note_es: "Lista de cuentas tuyas (correo, banco, redes, nube). Cambia las contraseñas desde un dispositivo al que él no acceda.",
  },
  {
    id: "transportation-plan",
    title_en: "Transportation plan",
    title_es: "Plan de transporte",
    note_en: "Even just knowing which friend or rideshare you’d call at 3 a.m. counts.",
    note_es: "Aunque solo sepas a qué amiga o app de transporte llamarías a las 3 de la mañana, cuenta.",
  },
  {
    id: "housing-options",
    title_en: "Housing options",
    title_es: "Opciones de vivienda",
    note_en: "One friend’s couch + one shelter number + one budget rental search. Three doors is enough.",
    note_es: "Un sofá de una amiga + un número de refugio + una búsqueda de renta económica. Tres puertas son suficientes.",
  },
];

// --------------------------- COMFORT CORNER --------------------------- //

export const COMFORT = {
  grounding_en: [
    "Press both feet firmly into the floor for ten seconds. Notice the support.",
    "Name five things you can see right now in colors only.",
    "Run cool water over your wrists for thirty seconds.",
    "Hold an ice cube in your palm until it melts halfway.",
    "Breathe out longer than you breathe in for five rounds.",
    "Put one hand on your chest and one on your belly. Feel both rise.",
    "Look out a window and find the furthest thing you can see.",
    "Hum a single low note until your breath runs out. Repeat three times.",
    "Touch four different textures within arm’s reach.",
    "Count backward from 100 by sevens. Slowly.",
  ],
  grounding_es: [
    "Presiona ambos pies firmes contra el suelo por diez segundos. Nota el soporte.",
    "Nombra cinco cosas que ves ahora mismo, solo por su color.",
    "Pasa agua fría por tus muñecas treinta segundos.",
    "Sostén un cubo de hielo en la palma hasta que se derrita a la mitad.",
    "Exhala más largo que inhalas durante cinco rondas.",
    "Una mano en el pecho, otra en el vientre. Siente cómo suben ambas.",
    "Mira por una ventana y encuentra el objeto más lejano que puedas ver.",
    "Tararea una sola nota baja hasta que se acabe el aire. Tres veces.",
    "Toca cuatro texturas distintas al alcance de tu brazo.",
    "Cuenta hacia atrás de 100 en sietes. Despacio.",
  ],
  soothe_en: [
    "Wrap yourself in the heaviest blanket you own.",
    "Warm a mug of milk or tea and hold it with both hands.",
    "Put on the softest item of clothing you own.",
    "Light a single candle and watch the flame for sixty seconds.",
    "Take a shower with the lights dim and the water warm.",
    "Press a warm compress to the back of your neck.",
    "Lie down with a pillow over your stomach. The weight calms the nervous system.",
    "Smell something you love — lavender, vanilla, citrus, eucalyptus.",
    "Move to a smaller room. Smaller spaces feel safer to a tired body.",
    "Put on quiet instrumental music and lower the volume until it’s nearly a whisper.",
  ],
  soothe_es: [
    "Envuélvete en la cobija más pesada que tengas.",
    "Calienta una taza de leche o té y sostenla con ambas manos.",
    "Ponte la prenda más suave que tengas.",
    "Enciende una sola vela y mira la llama por sesenta segundos.",
    "Date una ducha con la luz tenue y agua tibia.",
    "Aplica una compresa tibia en la nuca.",
    "Acuéstate con una almohada sobre el estómago. El peso calma al sistema nervioso.",
    "Huele algo que ames — lavanda, vainilla, cítricos, eucalipto.",
    "Múdate a un cuarto más pequeño. Un cuerpo cansado se siente más a salvo en lugares pequeños.",
    "Pon música instrumental tranquila y baja el volumen hasta que sea casi un susurro.",
  ],
  activities_en: [
    "Take a slow walk around one block. Not for steps. For air.",
    "Make a list of things that didn’t hurt today.",
    "Re-read three pages of a book you already love.",
    "Stretch your body in any direction it asks for, for ninety seconds.",
    "Doodle on paper with no plan. Five minutes.",
    "Reorganize one small drawer or shelf — slowly.",
    "Look at photos of yourself before this season of your life.",
    "Sit in your car with the engine off and the windows cracked. Music optional.",
    "Bake or cook one simple thing. Stay in the kitchen the whole time.",
    "Write a letter to someone you love and don’t send it.",
  ],
  activities_es: [
    "Camina despacio una cuadra. No por los pasos. Por el aire.",
    "Haz una lista de cosas que hoy no te dolieron.",
    "Relee tres páginas de un libro que ya amas.",
    "Estira tu cuerpo en cualquier dirección que pida, por noventa segundos.",
    "Garabatea en papel sin plan. Cinco minutos.",
    "Reorganiza un cajón o una repisa pequeña — despacio.",
    "Mira fotos tuyas de antes de esta temporada de tu vida.",
    "Siéntate en tu auto con el motor apagado y las ventanas entreabiertas. Música opcional.",
    "Hornea o cocina una cosa simple. Quédate toda la receta en la cocina.",
    "Escríbele una carta a alguien que amas y no la envíes.",
  ],
};
